import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, CheckCircle2, CircleDashed, FileText } from "lucide-react";
import { toast } from "sonner";
import { cliente, crmError } from "@/lib/crm";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function DocBox({ tipo, label, docs, onUpload }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const received = docs.length > 0;

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      await onUpload(tipo, file);
      toast.success("Documento caricato correttamente");
    } catch (ex) {
      toast.error(crmError(ex, "Caricamento non riuscito. Riprova."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`doc-box-${tipo}`} className="rounded-xl border border-[#E0E0E0] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-zr-blue">{label}</p>
          <p data-testid={`doc-status-${tipo}`} className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold ${received ? "text-[#2E7D32]" : "text-zr-grey"}`}>
            {received ? <CheckCircle2 className="h-3.5 w-3.5" /> : <CircleDashed className="h-3.5 w-3.5" />}
            {received ? "Ricevuto" : "Da caricare"}
          </p>
        </div>
        <button type="button" data-testid={`doc-upload-${tipo}`} disabled={busy} onClick={() => inputRef.current?.click()} className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-zr-green px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#43A047] disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Carica
        </button>
        <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={pick} data-testid={`doc-input-${tipo}`} />
      </div>
      {docs.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {docs.map((d) => (
            <li key={d.id} data-testid="doc-item" className="flex items-center gap-2 text-xs text-zr-grey">
              <FileText className="h-3.5 w-3.5 shrink-0 text-zr-blue" />
              <span className="truncate font-medium text-zr-blue">{d.filename}</span>
              <span className="shrink-0">· {fmtSize(d.size)} · {fmtDate(d.uploaded_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const ClienteDocumenti = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = () => cliente.documenti().then(setData).catch((e) => setErr(crmError(e)));
  useEffect(() => { load(); }, []);

  const upload = async (tipo, file) => {
    await cliente.uploadDocumento(tipo, file);
    await load();
  };

  return (
    <section data-testid="card-documenti" className="rounded-2xl bg-white p-6 shadow-[0_8px_30px_rgba(30,58,95,0.08)]">
      <h2 className="mb-1 flex items-center gap-2 text-base font-bold text-zr-blue md:text-lg">
        <Upload className="h-4 w-4 text-zr-green" /> Documenti da inviare
      </h2>
      <p className="mb-4 text-xs leading-relaxed text-zr-grey">
        Puoi caricare solo questi due documenti (PDF, JPG o PNG, max 10 MB; puoi caricare più file, es. fronte e retro).
        Tutti gli altri documenti della pratica vanno consegnati di persona allo studio.
      </p>
      {err && <p data-testid="documenti-error" className="text-sm font-medium text-[#C62828]">{err}</p>}
      {!data && !err && <Loader2 className="mx-auto my-6 h-6 w-6 animate-spin text-zr-green" />}
      {data && (
        <div className="grid gap-3 sm:grid-cols-2">
          {data.tipi_ammessi.map((t) => (
            <DocBox key={t.tipo} tipo={t.tipo} label={t.label} docs={data.documenti.filter((d) => d.tipo === t.tipo)} onUpload={upload} />
          ))}
        </div>
      )}
    </section>
  );
};

export const ClienteProiezione = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    cliente.proiezioni().then((d) => setList(d.proiezioni || [])).catch(() => {});
  }, []);
  if (!list.length) return null;
  return (
    <section data-testid="card-proiezione" className="rounded-2xl bg-white p-6 shadow-[0_8px_30px_rgba(30,58,95,0.08)]">
      <h2 className="mb-1 flex items-center gap-2 text-base font-bold text-zr-blue md:text-lg">
        <FileText className="h-4 w-4 text-zr-green" /> Proiezione del Piano
      </h2>
      <p className="mb-4 text-xs text-zr-grey">Documento indicativo preparato dal tuo advisor. La versione più recente è la prima.</p>
      <ul className="space-y-2">
        {list.map((p, i) => (
          <li key={p.id} data-testid="proiezione-item" className="flex items-center justify-between gap-3 rounded-xl border border-[#E0E0E0] px-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-zr-blue">{p.titolo || p.filename}{i === 0 && <span className="ml-2 rounded-full bg-zr-light px-2 py-0.5 text-[10px] font-bold uppercase text-zr-blue">Ultima</span>}</p>
              <p className="text-xs text-zr-grey">{fmtDate(p.created_at)}{p.size ? ` · ${fmtSize(p.size)}` : ""}</p>
            </div>
            <a href={cliente.proiezioneUrl(p.id)} target="_blank" rel="noreferrer" data-testid="proiezione-open" className="shrink-0 rounded-xl bg-zr-green px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#43A047]">
              Apri PDF
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};
